import type { CallItem, CallsPage } from '../../../services/reports.service';
import Icon from '../../../components/forms/icon.component';
import { CPD_COLORS } from '../dashboard/dashboard.charts';
import { VERDICT_LABEL, fmtDateTime, fmtSecs } from './reports.types';

const PAGE_SIZES = [50, 100, 200, 500];

/** Tabla de llamadas con paginación en servidor (limit/offset del backend). */
export default function CallsTable({
  page,
  loading,
  limit,
  onLimit,
  onOffset,
  onDetail
}: {
  page: CallsPage | null;
  loading: boolean;
  limit: number;
  onLimit: (n: number) => void;
  onOffset: (n: number) => void;
  onDetail: (c: CallItem) => void;
}) {
  const total = page?.total ?? 0;
  const offset = page?.offset ?? 0;
  const current = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  const verdictBadge = (v: string) => {
    const color =
      v === 'human'
        ? CPD_COLORS.human
        : v === 'voicemail'
          ? CPD_COLORS.voicemail
          : v === 'bypass'
            ? CPD_COLORS.bypassed
            : CPD_COLORS.unknown;
    return (
      <span className="badge fs-8 fw-bold" style={{ backgroundColor: `${color}22`, color }}>
        {VERDICT_LABEL[v] ?? (v || '—')}
      </span>
    );
  };

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-row-dashed table-hover align-middle fs-7 gy-3">
          <thead>
            <tr className="text-muted fw-bold text-uppercase fs-8">
              <th>Inicio</th>
              <th>Operador</th>
              <th>Ruta</th>
              <th>Destino</th>
              <th>Veredicto</th>
              <th>Detect</th>
              <th>Hangup</th>
              <th className="text-end">Billsec</th>
              <th className="text-end">Duración</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-10">
                  Consultando ClickHouse…
                </td>
              </tr>
            )}
            {!loading && page && page.items.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center text-muted py-10">
                  Sin llamadas con esos filtros
                </td>
              </tr>
            )}
            {!loading &&
              page?.items.map((c) => (
                <tr key={c.callUuid} style={{ cursor: 'pointer' }} onClick={() => onDetail(c)}>
                  <td className="text-nowrap">{fmtDateTime(c.startedAt)}</td>
                  <td className="fw-bold text-uppercase">{c.operatorId || '—'}</td>
                  <td>{c.routeGroupId ?? '—'}</td>
                  <td className="font-monospace">{c.bNumber || '—'}</td>
                  <td>{verdictBadge(c.cpdVerdict)}</td>
                  <td className="text-muted">{c.cpdDetectMs ? `${c.cpdDetectMs} ms` : '—'}</td>
                  <td>
                    <code className="fs-8">{c.hangupCause || '—'}</code>
                    {c.sipTermStatus ? (
                      <span className="text-muted fs-9 ms-1">({c.sipTermStatus})</span>
                    ) : null}
                  </td>
                  <td className="text-end">{fmtSecs(c.billsec)}</td>
                  <td className="text-end">{fmtSecs(c.duration)}</td>
                  <td className="text-end">
                    <Icon icon="ki-right" size="fs-5 text-gray-400" />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-4 fs-7">
        <div className="d-flex align-items-center gap-2 text-muted">
          <span>Mostrar</span>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => onLimit(Number(e.target.value))}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>· {total.toLocaleString('es-MX')} llamadas</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={offset === 0 || loading}
            onClick={() => onOffset(Math.max(0, offset - limit))}
          >
            <Icon icon="ki-left" size="fs-5" />
          </button>
          <span className="text-muted">
            página {current} de {pages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-light"
            disabled={offset + limit >= total || loading}
            onClick={() => onOffset(offset + limit)}
          >
            <Icon icon="ki-right" size="fs-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
